import { FC } from "react";
import { Button } from "antd";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { XIcon } from "src/assets/Icons";
import { RowSeat, SeatType } from "src/Modules/Flights/constants/JourneyDetails";
import { useSSRContext } from "../../../../context/SSRContext";
import { compactNumber } from "src/utility";
import Badge from "src/components/Banner/Banner";
import { track } from "@amplitude/analytics-browser";
import SeatUseIcon from "../SeatIcon/SeatUseIcon";
import { SEAT_FILL_CLASSNAME, getSeatVisualState } from "./seatVisualState";

const EXIT_ROW_TYPES = ["WindowExitRow", "AisleExitRow", "MiddleExitRow", "WindowWingExitRow"];

const Seat: FC<{
  seatData: SeatType;
  colStart: number;
  onClick: any;
  reservedSeats: string[];
}> = ({
  seatData,
  colStart,
  onClick,
  reservedSeats,
}) => {
  const { t } = useTranslation();
  const isAvailable = seatData?.availabilityType === "Open";
  const isSelected = reservedSeats.includes(seatData.code);
  const isExitRow = EXIT_ROW_TYPES.includes(seatData.seatSubType);
  const showFreeLabel = seatData?.price === 0 && seatData?.availabilityType === "Open";
  const { getSSRItem } = useSSRContext();
  const handleClick = () => {
    const seatSelected = Object.keys(getSSRItem("seats"))?.[0];
    if (seatSelected === seatData.code) {
      track("Seat Unselected",{
        ...seatData
      })
      return onClick({});
    }
    if (isSelected) {
      return;
    }
    track("Seat Selected",{
      ...seatData
    })
    onClick({ [seatData.code]: seatData });

  };

  return (
    <Badge
      degree={-45}
      title={showFreeLabel && <span className="text-[clamp(.2rem,.8cqw,1.3rem)] font-semibold text-font-success" >{t("Flights.details.ssr.free")}</span>}
      distanceFromCenter={-90}
      style={{
        gridRow: `1 / span 1`,
        gridColumn: `${colStart} / span 1`,
      }}
      bannerClassname="bg-background-success z-10"
    >
      <div
        className="justify-center @container flex aspect-square w-full items-center min-w-[1rem] flex! items-center! justify-center!"
      >
        <Button
          className={classNames(
            "relative! m-0! p-0! flex! h-full! w-full! flex-1! cursor-pointer! select-none! items-center! justify-center!",
            "aspect-square! bg-transparent! border-none! shadow-none! transition-colors! rounded-sm!",
          )}
          disabled={!isAvailable}
          onClick={handleClick}
        >
          <SeatUseIcon
            className={classNames(
              "absolute! inset-0! h-full! w-full! -scale-y-100!",
              SEAT_FILL_CLASSNAME[getSeatVisualState(isAvailable, isSelected, isExitRow)],
            )}
          />
          <div
            className={classNames(
              "font-poppins! relative! z-10! flex! flex-col! items-center! text-xs! font-semibold! text-[clamp(.1rem,.8cqw,1rem)]!",
              isAvailable ? "text-font-default!" : "text-font-disabled!",
            )}
          >
            {isAvailable ? (
              <>
                <div className="font-bold text-[clamp(.7rem,.8cqw,1rem)]">{compactNumber(seatData.price)}</div>
                <div className="font-normal text-[clamp(.4rem,.7cqw,.8rem)]">{seatData.currency}</div>
              </>
            ) : (
              <XIcon className="h-3 w-3 md:h-4 md:w-4" />
            )}
          </div>
        </Button>
      </div>
    </Badge>
  );
};

const Gap: FC<{ colStart: number; rowNo?: string }> = ({ colStart, rowNo }) => {
  return (
    <div
      style={{
        gridRowStart: 1,
        gridColumn: `${colStart} / span 1`,
      }}
      className="text-font-minimal font-poppins flex items-center justify-center text-xs"
    >
      {rowNo}
    </div>
  );
};

const RowItem: FC<{
  col: [number, string];
  row: RowSeat;
  onClick: any;
  reservedSeats: string[];
}> = ({
  col,
  row,
  onClick,
  reservedSeats,
}) => {
  const isGap = col[1] === "GAP";
  const seatData = row.seats.find((element) => element.seatNo === col[1]);
  if (isGap) return <Gap colStart={col[0]} rowNo={row.seats?.[0]?.rowNo} />;
  if (seatData) return <Seat colStart={col[0]} seatData={seatData} onClick={onClick} reservedSeats={reservedSeats} />;

  return null;
};

export default RowItem;
